import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, ToggleButton,
  ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import SalesItemSearchSelect from '../sales/SalesItemSearchSelect';
import { Item } from '../../interfaces/item.interface';
import { KitService } from '../../services/kit.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';

/** F6c: edits a KIT item's components; "Package product" is the pack-of-N preset (one component, quantity N). */

const MAX_COMPONENTS = 20;

interface Row { componentItemId: string; quantity: string; label?: string }

interface Props {
  open: boolean;
  kit: Item | null;
  onClose: () => void;
}

type Mode = 'KIT' | 'PACK';

export const validateKitRows = (rows: Row[]): string | null => {
  if (rows.length === 0) return 'Add at least one component';
  if (rows.length > MAX_COMPONENTS) return `A kit holds at most ${MAX_COMPONENTS} components`;
  const seen = new Set<string>();
  for (const r of rows) {
    if (!r.componentItemId) return 'Choose an item for every component';
    if (!/^\d+$/.test(r.quantity.trim()) || Number(r.quantity) <= 0) return 'Component quantities must be whole numbers above zero';
    if (seen.has(r.componentItemId)) return 'Each component item can appear only once';
    seen.add(r.componentItemId);
  }
  return null;
};

/** "CODE - Name", the app's item label convention (same as the item pickers). */
export const codeNameLabel = (code?: string | null, name?: string | null): string =>
  [code, name].filter(Boolean).join(' - ');

/** A package product: exactly one component with a quantity above one. */
export const isPackOfN = (rows: Row[]): boolean =>
  rows.length === 1 && /^\d+$/.test(rows[0].quantity) && Number(rows[0].quantity) > 1;

const KitComponentsDialog: React.FC<Props> = ({ open, kit, onClose }) => {
  const toast = useToast();
  const [rows, setRows] = useState<Row[]>([]);
  const [mode, setMode] = useState<Mode>('KIT');
  const [packItem, setPackItem] = useState('');
  const [packLabel, setPackLabel] = useState('');
  const [packSize, setPackSize] = useState('12');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const institutionId = AuthService.getSelectedInstitution() as string;

  useEffect(() => {
    if (!open || !kit?.id) return;
    let live = true;
    setError(null);
    setLoading(true);
    KitService.get(institutionId, kit.id).then(current => {
      if (!live) return;
      const loaded = current.components.map(c => ({
        componentItemId: c.componentItemId, quantity: String(c.quantity),
        label: codeNameLabel(c.code, c.name),
      }));
      setRows(loaded.length ? loaded : [{ componentItemId: '', quantity: '1' }]);
      // A pack of N (one component, quantity N > 1) reopens in the package preset it was saved from.
      const pack = isPackOfN(loaded);
      setMode(pack ? 'PACK' : 'KIT');
      setPackItem(pack ? loaded[0].componentItemId : '');
      setPackLabel(pack ? loaded[0].label : '');
      setPackSize(pack ? loaded[0].quantity : '12');
    }).catch(err => {
      if (live) toast.showApiError(err, 'Failed to load kit components');
    }).finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, kit?.id]);

  // Stock items are searched on the server as the user types; a picked component keeps its label for display.
  const itemLabel = (i: Item | null) => (i ? codeNameLabel(i.itemCode, i.itemName) : '');

  const setRow = (idx: number, patch: Partial<Row>) => setRows(rs => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const save = async () => {
    if (!kit?.id) return;
    const wanted: Row[] = mode === 'PACK' ? [{ componentItemId: packItem, quantity: packSize }] : rows;
    const problem = validateKitRows(wanted);
    if (problem) { setError(problem); return; }
    setSaving(true);
    try {
      await KitService.replace(institutionId, kit.id,
        wanted.map(r => ({ componentItemId: r.componentItemId, quantity: Number(r.quantity) })));
      toast.showSuccess('Kit components saved');
      onClose();
    } catch (err) {
      toast.showApiError(err, 'Failed to save kit components');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog data-help-page="store.kit.page" open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Kit components{kit ? `: ${codeNameLabel(kit.itemCode, kit.itemName)}` : ''}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A kit holds no stock of its own. Delivering one kit relieves each component below from the store; posted
          challans keep what they relieved, so later edits here never change them.
        </Typography>
        <ToggleButtonGroup exclusive size="small" value={mode} onChange={(_, m) => m && setMode(m)} sx={{ mb: 2 }}>
          <ToggleButton data-help="store.kit.mode.kit" value="KIT" data-testid="kit-mode-kit">Kit (several components)</ToggleButton>
          <ToggleButton data-help="store.kit.mode.pack" value="PACK" data-testid="kit-mode-pack">Package product (pack of N)</ToggleButton>
        </ToggleButtonGroup>
        {error && <Alert severity="error" sx={{ mb: 2 }} data-testid="kit-error">{error}</Alert>}
        {loading ? <Typography>Loading…</Typography> : mode === 'PACK' ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: '1 1 320px', maxWidth: 520 }} data-help="store.kit.field.packItem">
              <SalesItemSearchSelect kind="STOCK" value={packItem || null} valueLabel={packLabel || undefined}
                onChange={(id, item) => { setPackItem(id); setPackLabel(itemLabel(item)); }}
                label="Item in the pack" size="medium" fullWidth data-testid="kit-pack-item" />
            </Box>
            <TextField data-help="store.kit.field.packSize" label="Pack of (N)" value={packSize} onChange={e => setPackSize(e.target.value)}
              sx={{ flex: '0 1 140px', maxWidth: 180 }} inputProps={{ inputMode: 'numeric', 'data-testid': 'kit-pack-size' }} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {rows.map((r, idx) => (
              <Box key={idx} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }} data-testid={`kit-row-${idx}`}>
                <Box sx={{ flex: '1 1 320px', maxWidth: 520 }} data-help="store.kit.field.component">
                  <SalesItemSearchSelect kind="STOCK" value={r.componentItemId || null} valueLabel={r.label || undefined}
                    onChange={(id, item) => setRow(idx, { componentItemId: id, label: itemLabel(item) || r.label })}
                    label={`Component ${idx + 1}`} fullWidth data-testid={`kit-component-${idx}`} />
                </Box>
                <TextField size="small" data-help="store.kit.field.qty" label="Qty per kit" value={r.quantity}
                  onChange={e => setRow(idx, { quantity: e.target.value })}
                  sx={{ flex: '0 1 120px', maxWidth: 160 }} inputProps={{ inputMode: 'numeric', 'data-testid': `kit-qty-${idx}` }} />
                <Tooltip title="Remove component">
                  <IconButton data-help="store.kit.remove" aria-label={`Remove component ${idx + 1}`} onClick={() => setRows(rs => rs.filter((_, i) => i !== idx))}>
                    <Delete />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
            <Box>
              <Button data-help="store.kit.add" size="small" disabled={rows.length >= MAX_COMPONENTS}
                onClick={() => setRows(rs => [...rs, { componentItemId: '', quantity: '1' }])}>
                Add component
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button data-help="store.kit.cancel" onClick={onClose}>Cancel</Button>
        <Button data-help="store.kit.save" variant="contained" onClick={save} disabled={saving || loading} data-testid="kit-save">Save components</Button>
      </DialogActions>
    </Dialog>
  );
};

export default KitComponentsDialog;
